import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, Loader2, Edit2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function WeaverMasterPage() {
  const [weavers, setWeavers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [editWeaver, setEditWeaver] = useState(null);
  const [editName, setEditName] = useState("");
  const [selectedWeaver, setSelectedWeaver] = useState(null);
  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rateForm, setRateForm] = useState({ count: "", quality: "", price_per_unit: "", sizing_charge_per_unit: "" });
  const [yarnData, setYarnData] = useState({ counts: [], qualities: [], items: [] });

  const fetchWeavers = useCallback(async () => {
    try {
      const res = await api.get("/weavers");
      setWeavers(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  const fetchYarnData = useCallback(async () => {
    try {
      const res = await api.get("/yarn-master/unique-values");
      setYarnData(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchWeavers(); fetchYarnData(); }, [fetchWeavers, fetchYarnData]);

  const fetchRates = useCallback(async (wid) => {
    setRatesLoading(true);
    try {
      const res = await api.get(`/weaver-rates/${wid}`);
      setRates(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRatesLoading(false); }
  }, []);

  const addWeaver = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/weavers", { name: newName.trim() });
      toast.success("Weaver added");
      setNewName("");
      setAddOpen(false);
      fetchWeavers();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateWeaver = async () => {
    if (!editName.trim() || !editWeaver) return;
    try {
      await api.put(`/weavers/${editWeaver.id}`, { name: editName.trim() });
      toast.success("Weaver updated");
      setEditWeaver(null);
      fetchWeavers();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteWeaver = async (id) => {
    try {
      await api.delete(`/weavers/${id}`);
      toast.success("Weaver deleted");
      if (selectedWeaver?.id === id) { setSelectedWeaver(null); setRates([]); }
      fetchWeavers();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const selectWeaver = (w) => {
    setSelectedWeaver(w);
    fetchRates(w.id);
    setRateForm({ count: "", quality: "", price_per_unit: "", sizing_charge_per_unit: "" });
  };

  const addRate = async () => {
    if (!rateForm.count || !rateForm.quality || !rateForm.price_per_unit) return;
    try {
      await api.post("/weaver-rates", {
        weaver_id: selectedWeaver.id,
        count: rateForm.count,
        quality: rateForm.quality,
        price_per_unit: parseFloat(rateForm.price_per_unit),
        sizing_charge_per_unit: parseFloat(rateForm.sizing_charge_per_unit || 0),
      });
      toast.success("Rate saved");
      setRateForm({ count: "", quality: "", price_per_unit: "", sizing_charge_per_unit: "" });
      fetchRates(selectedWeaver.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteRate = async (id) => {
    try {
      await api.delete(`/weaver-rates/${id}`);
      toast.success("Rate deleted");
      fetchRates(selectedWeaver.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const getQualities = (count) => {
    if (!count) return yarnData.qualities || [];
    return [...new Set(yarnData.items.filter((i) => i.count === count).map((i) => i.quality))];
  };

  return (
    <div className="space-y-6" data-testid="weaver-master-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Weaver Master
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Manage weavers and their issued yarn rates.
        </p>
      </div>

      <div className="flex gap-6">
        {/* Left: Weaver list */}
        <div className="w-64 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Weavers</p>
            <Button size="sm" data-testid="add-weaver-button" className="h-7 bg-[#C2410C] hover:bg-[#9A3412] text-white text-xs" onClick={() => setAddOpen(true)}>
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-stone-400" /></div>
            ) : weavers.length === 0 ? (
              <div className="p-6 text-center text-sm text-stone-400">No weavers yet</div>
            ) : (
              weavers.map((w) => (
                <div
                  key={w.id}
                  data-testid={`weaver-item-${w.id}`}
                  className={`flex items-center justify-between px-3 py-2.5 cursor-pointer border-b border-stone-100 last:border-0 transition-colors ${
                    selectedWeaver?.id === w.id ? "bg-orange-50 text-[#C2410C]" : "hover:bg-stone-50"
                  }`}
                  onClick={() => selectWeaver(w)}
                >
                  <span className="text-sm font-medium truncate flex-1">{w.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button className="p-1 hover:text-blue-600" onClick={(e) => { e.stopPropagation(); setEditWeaver(w); setEditName(w.name); }}>
                      <Edit2 className="h-3 w-3" />
                    </button>
                    <button className="p-1 hover:text-red-600" onClick={(e) => { e.stopPropagation(); deleteWeaver(w.id); }}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                    <ChevronRight className="h-3.5 w-3.5 text-stone-300" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Yarn rates for selected weaver */}
        <div className="flex-1 min-w-0">
          {!selectedWeaver ? (
            <div className="bg-white border border-stone-200 rounded-lg p-12 text-center text-stone-400 text-sm">
              Select a weaver to manage their yarn rates
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">{selectedWeaver.name}</h2>
                <p className="text-xs text-stone-500">Issued yarn rates for this weaver</p>
              </div>

              {/* Add rate form */}
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Add / Update Rate</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Count</Label>
                    <Select value={rateForm.count} onValueChange={(v) => setRateForm({ ...rateForm, count: v, quality: "" })}>
                      <SelectTrigger data-testid="rate-count-select" className="h-9"><SelectValue placeholder="Count" /></SelectTrigger>
                      <SelectContent>{(yarnData.counts || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Quality</Label>
                    <Select value={rateForm.quality} onValueChange={(v) => setRateForm({ ...rateForm, quality: v })}>
                      <SelectTrigger data-testid="rate-quality-select" className="h-9"><SelectValue placeholder="Quality" /></SelectTrigger>
                      <SelectContent>{getQualities(rateForm.count).map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Price/Unit</Label>
                    <Input data-testid="rate-price-input" type="number" step="0.01" className="h-9" value={rateForm.price_per_unit} onChange={(e) => setRateForm({ ...rateForm, price_per_unit: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Sizing/Unit</Label>
                    <Input data-testid="rate-sizing-input" type="number" step="0.01" className="h-9" value={rateForm.sizing_charge_per_unit} onChange={(e) => setRateForm({ ...rateForm, sizing_charge_per_unit: e.target.value })} />
                  </div>
                  <div className="flex items-end">
                    <Button data-testid="add-rate-button" className="h-9 bg-[#C2410C] hover:bg-[#9A3412] text-white w-full" onClick={addRate}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Save Rate
                    </Button>
                  </div>
                </div>
              </div>

              {/* Rates table */}
              <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-2.5">Count</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-2.5">Quality</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-2.5 text-right">Price/Unit</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-2.5 text-right">Sizing/Unit</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-2.5 w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ratesLoading ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-stone-400" /></TableCell></TableRow>
                    ) : rates.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-8 text-stone-400 text-sm">No rates added yet</TableCell></TableRow>
                    ) : (
                      rates.map((r) => (
                        <TableRow key={r.id} data-testid={`rate-row-${r.id}`}>
                          <TableCell className="px-4 py-2 text-sm">{r.count}</TableCell>
                          <TableCell className="px-4 py-2 text-sm">{r.quality}</TableCell>
                          <TableCell className="px-4 py-2 text-sm text-right font-mono">{r.price_per_unit}</TableCell>
                          <TableCell className="px-4 py-2 text-sm text-right font-mono">{r.sizing_charge_per_unit || 0}</TableCell>
                          <TableCell className="px-4 py-2">
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-stone-400 hover:text-red-600" onClick={() => deleteRate(r.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Weaver Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Weaver</DialogTitle>
            <DialogDescription>Enter the weaver's name</DialogDescription>
          </DialogHeader>
          <Input data-testid="weaver-name-input" placeholder="Weaver name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addWeaver(); }} autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-add-weaver" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={addWeaver}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Weaver Dialog */}
      <Dialog open={!!editWeaver} onOpenChange={() => setEditWeaver(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Weaver</DialogTitle>
          </DialogHeader>
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") updateWeaver(); }} autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditWeaver(null)}>Cancel</Button>
            <Button className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={updateWeaver}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
